import fuzzysort from 'fuzzysort';
import { Search } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TagResponse } from '@/api/client';
import { TagChip } from '@/components/TagDisplay';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useTagsQuery } from '@/hooks/useGraphQLQueries';

// No-op for backwards compatibility - urql's graphcache handles invalidation automatically
export function invalidateTagSearchCache(): void {
	// urql's normalized cache is invalidated via mutations and subscriptions
}

interface TagSearchModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSelectTag: (tagName: string) => void;
	activeTagFilters: string[];
}

// Prepared tag for fuzzysort
interface PreparedTag {
	tag: TagResponse;
	name: string;
	value: string; // The part after the prefix (for matching "hello" in "instagram:hello")
}

// Extract value part from prefixed tag (for fuzzy matching)
function getTagValue(name: string): string {
	return name.includes(':') ? name.split(':').slice(1).join(':') : name;
}

function TagSearchResult({
	tag,
	isSelected,
	isActive,
	onClick,
}: {
	tag: TagResponse;
	isSelected: boolean;
	isActive: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				'w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors',
				isActive && 'bg-accent',
				isSelected && 'opacity-50'
			)}
		>
			{/* Tag chip - no onClick so it's not interactive */}
			<div className="pointer-events-none">
				<TagChip
					tag={{
						id: tag.id,
						name: tag.name,
						color: tag.color,
						icon: tag.icon ?? null,
						linkCount: tag.linkCount,
						isPersonal: tag.isPersonal,
					}}
					size="md"
				/>
			</div>
			<span className="flex-1" />
			<span className="text-sm text-muted-foreground tabular-nums">
				{tag.linkCount ?? 0}
			</span>
			{isSelected && (
				<span className="text-xs text-muted-foreground">(active)</span>
			)}
		</button>
	);
}

export const TagSearchModal = memo(function TagSearchModal({
	open,
	onOpenChange,
	onSelectTag,
	activeTagFilters,
}: TagSearchModalProps) {
	const [query, setQuery] = useState('');
	const [activeIndex, setActiveIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	// Use GraphQL query for tags - urql's cache handles deduplication across instances
	const { tags: allTags } = useTagsQuery({ includeNsfw: true });

	// Reset state when modal opens
	useEffect(() => {
		if (open) {
			setQuery('');
			setActiveIndex(0);
			// Focus input after a brief delay
			setTimeout(() => inputRef.current?.focus(), 50);
		}
	}, [open]);

	// Prepare tags for fuzzysort
	const preparedTags = useMemo<PreparedTag[]>(
		() =>
			allTags.map((tag: TagResponse) => ({
				tag,
				name: tag.name,
				value: getTagValue(tag.name),
			})),
		[allTags]
	);

	// Active filter set for quick lookup
	const activeFilterSet = useMemo(
		() => new Set(activeTagFilters),
		[activeTagFilters]
	);

	// Filter and sort tags
	const filteredTags = useMemo(() => {
		if (!query.trim()) {
			// No query: show all tags sorted by link count
			return [...allTags].sort((a, b) => (b.linkCount ?? 0) - (a.linkCount ?? 0));
		}

		// Fuzzy search across name and value
		const results = fuzzysort.go(query, preparedTags, {
			keys: ['name', 'value'],
			threshold: -10000, // Allow weak matches (we want prefix matching)
			limit: 50,
		});

		return results.map((r) => r.obj.tag);
	}, [query, allTags, preparedTags]);

	// Reset active index when results change
	useEffect(() => {
		setActiveIndex(0);
	}, [filteredTags.length]);

	// Scroll active item into view
	useEffect(() => {
		const list = listRef.current;
		if (!list) return;
		const activeItem = list.children[activeIndex] as HTMLElement;
		if (activeItem) {
			activeItem.scrollIntoView({ block: 'nearest' });
		}
	}, [activeIndex]);

	// Handle tag selection
	const handleSelect = useCallback(
		(tagName: string) => {
			onSelectTag(tagName);
			onOpenChange(false);
		},
		[onSelectTag, onOpenChange]
	);

	// Keyboard navigation
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setActiveIndex((i) => Math.min(i + 1, filteredTags.length - 1));
					break;
				case 'ArrowUp':
					e.preventDefault();
					setActiveIndex((i) => Math.max(i - 1, 0));
					break;
				case 'Enter':
					e.preventDefault();
					// Select active item, or first item if activeIndex is somehow invalid
					const targetTag = filteredTags[activeIndex] ?? filteredTags[0];
					if (targetTag) {
						handleSelect(targetTag.name);
					}
					break;
				case 'Escape':
					e.preventDefault();
					onOpenChange(false);
					break;
			}
		},
		[filteredTags, activeIndex, handleSelect, onOpenChange]
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden">
				{/* Search input */}
				<div className="flex items-center gap-2 px-3 border-b">
					<Search className="size-4 text-muted-foreground shrink-0" />
					<Input
						ref={inputRef}
						type="text"
						placeholder="Search tags... (e.g., insta:hello)"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={handleKeyDown}
						className="border-0 focus-visible:ring-0 px-0 h-12"
					/>
					<kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-xs text-muted-foreground">
						ESC
					</kbd>
				</div>

				{/* Results list */}
				<div ref={listRef} className="max-h-[400px] overflow-y-auto p-2">
					{filteredTags.length === 0 ? (
						<div className="py-8 text-center text-sm text-muted-foreground">
							{query ? 'No tags match your search' : 'No tags available'}
						</div>
					) : (
						filteredTags.map((tag, index) => (
							<TagSearchResult
								key={tag.id}
								tag={tag}
								isSelected={activeFilterSet.has(tag.name)}
								isActive={index === activeIndex}
								onClick={() => handleSelect(tag.name)}
							/>
						))
					)}
				</div>

				{/* Footer hint */}
				<div className="px-3 py-2 border-t bg-muted/30 text-xs text-muted-foreground flex items-center gap-4">
					<span>
						<kbd className="px-1 py-0.5 rounded bg-muted border text-[10px]">↑↓</kbd> navigate
					</span>
					<span>
						<kbd className="px-1 py-0.5 rounded bg-muted border text-[10px]">Enter</kbd> select
					</span>
					<span>
						<kbd className="px-1 py-0.5 rounded bg-muted border text-[10px]">ESC</kbd> close
					</span>
				</div>
			</DialogContent>
		</Dialog>
	);
});
